import { inject, Injector, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';

import { translate, TranslationTokens } from '@ideals/components/translate';
import { uniqueId } from '@ideals/utils/unique-id';

import { ContainerComponent } from './container';
import { ToastRef } from './toast-ref';

import { ToastComponent } from './toast.component';
import { ToastMessage, ToastMessageButton } from './toast.model';

const LIFE_WITHOUT_BUTTONS = 5000;
const LIFE_WITH_BUTTONS = 10000;

interface Config {
  readonly buttons?: ToastMessageButton[];
  readonly closeable?: boolean;
  readonly icon?: string;
  readonly id?: string;
  readonly life?: number;
  readonly message?: TranslationTokens;
  readonly progress$?: Observable<number>;
  readonly severity?: 'success' | 'info' | 'warn' | 'error';
  readonly sticky?: boolean;
}

@Service()
export class ToastService {
  readonly #injector = inject(Injector);
  readonly #translateService = inject(TranslateService);

  readonly #toastRefs = new Map<string, ToastRef>();

  error(message: TranslationTokens, config: Config = {}): ToastRef {
    return this.#add({
      icon: 'icon-exclamation-octagon',
      ...config,
      message,
      severity: 'error',
    });
  }

  info(message: TranslationTokens, config: Config = {}): ToastRef {
    return this.#add({
      icon: 'icon-info-circle',
      ...config,
      message,
      severity: 'info',
    });
  }

  progress(message: TranslationTokens, config: Config = {}): ToastRef {
    return this.#add({
      icon: 'progress-circle',
      ...config,
      message,
      severity: 'info',
    });
  }

  success(message: TranslationTokens, config: Config = {}): ToastRef {
    return this.#add({
      icon: 'icon-check-circle',
      ...config,
      message,
      severity: 'success',
    });
  }

  warn(message: TranslationTokens, config: Config = {}): ToastRef {
    return this.#add({
      icon: 'icon-exclamation-triangle',
      ...config,
      message,
      severity: 'warn',
    });
  }

  #add(config: Config): ToastRef {
    const buttonsLength = config.buttons?.length ?? 0;
    const detail = translate(this.#translateService, config.message!);
    const id = config.id ?? (buttonsLength > 0 ? uniqueId() : detail);

    const message: ToastMessage = {
      buttons: config.buttons ?? [],
      closable: config.closeable ?? true,
      detail,
      icon: config.icon!,
      id,
      life: config.life ?? (config.buttons ? LIFE_WITH_BUTTONS : LIFE_WITHOUT_BUTTONS),
      progress$: config.progress$,
      severity: config.severity!,
      sticky: config.sticky ?? false,
    };

    this.#toastRefs.get(id)?.close();

    const outlet = ContainerComponent.create(this.#injector);
    const toastComponentRef = outlet.viewContainerRef().createComponent(ToastComponent);

    toastComponentRef.setInput('message', message);
    toastComponentRef.instance.closed.subscribe(() => {
      this.#toastRefs.delete(id);
      toastComponentRef.destroy();
    });

    const toastRef = new ToastRef(toastComponentRef.instance.closed, () => {
      toastComponentRef.instance.close();
    });

    this.#toastRefs.set(id, toastRef);

    return toastRef;
  }
}
